import { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ScheduleIcon from '@mui/icons-material/Schedule'
import TuneIcon from '@mui/icons-material/Tune'
import GavelIcon from '@mui/icons-material/Gavel'
import BalanceIcon from '@mui/icons-material/Balance'
import Slider from '@mui/material/Slider'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SpeedIcon from '@mui/icons-material/Speed'
import FactoryIcon from '@mui/icons-material/Factory'
import InventoryIcon from '@mui/icons-material/Inventory2'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import CountertopsIcon from '@mui/icons-material/Countertops'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import PercentIcon from '@mui/icons-material/Percent'
import RemoveIcon from '@mui/icons-material/Remove'
import BarChartIcon from '@mui/icons-material/BarChart'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RunFailure, toastRunFailure } from './RunFailureDetails'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import {
  ConfigurationSettings,
  SchedulerRunConfig,
  WeekResult,
  expandNotProducedKeys as expandNotProducedKeysShared,
  parseConsolidatedWeeks,
  useApplyDontStartBeforeStagger,
  useConfigurationSettings,
  useDataStatus,
  usePermissions,
  usePlanConfigurationSettings,
  useRunSchedulerDirect,
  useScheduleProductionRequirements,
  useScheduleSettings,
} from './Scheduler.data'
import { useQueryClient } from 'react-query'
import { makespanForWeek } from './makespan'
import { weekBaseHourFor } from 'Features/SchedulerView/ClipToWeek'
import { ScheduleDataVersionsPanel } from './ScheduleDataVersions.view'
import { ScheduleSettingsSnapshotView } from './ScheduleSettingsSnapshot.view'
import { ScheduleDemandAdjustments } from './ScheduleDemandAdjustments.view'
import { useSchedulerData } from 'Features/SchedulerView/SchedulerView.data'
import { ScheduleFullGanttView } from './ScheduleFullGantt.view'
import { SchedulerValidationPanel } from './SchedulerValidation.view'
import {
  noticesToValidationResults,
  runAllValidations,
  ValidationResult,
} from './SchedulerValidation.utils'
import { SchedulerComparison } from './SchedulerComparison.view'
import { SchedulerCommentsPanel } from './SchedulerComments.view'
import {
  useSchedulerComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
} from './Scheduler.data'
import { useCurrentUserEmail } from './ScheduleComments.anchor'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import SendIcon from '@mui/icons-material/Send'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'

const PANEL_WIDTH = 650
export const HOURS_PER_WEEK = 168

// ─── Multi-week button persistence ────────────────────────────────────────
// The Multi-week button's state ('off' | 'selecting' | 'applied') and the
// selected-weeks Set must survive a browser reload — the user can be
// mid-selection or have an applied stagger, and the in-memory useState was
// being lost. The server-side snapshot (`consolidated_weeks` on
// SchedulerSettingsDelta) only covers the post-run 'applied' case; selecting
// state and explicit reverts need a client-side store.
// Exported so SchedulerInput.page.tsx can read the same state to enrich its
// own run-scheduler call with consolidated_weeks / time_horizon.
export type StoredMultiWeek = {
  state: 'off' | 'selecting' | 'applied'
  weeks: number[]
}

export const readStoredMultiWeek = (key: string): StoredMultiWeek | null => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      parsed &&
      typeof parsed === 'object' &&
      'state' in parsed &&
      'weeks' in parsed &&
      Array.isArray((parsed as StoredMultiWeek).weeks) &&
      (parsed as StoredMultiWeek).weeks.every((n) => typeof n === 'number')
    ) {
      return parsed as StoredMultiWeek
    }
  } catch {
    // Malformed JSON or localStorage unavailable — fall through to null.
  }
  return null
}

const writeStoredMultiWeek = (key: string, value: StoredMultiWeek) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage may be unavailable (private mode, quota); silent failure
    // is acceptable — the user just loses persistence for that session.
  }
}

type BatchCommentTarget = {
  productCode: string
  resourceName: string
  resourceType: 'kitchen' | 'line'
  startTime: number
  endTime: number
}

type ScheduleControlPanelProps = {
  schedulerId: string
  iteration: string
  selectedWeek: number
  onWeekChange: (week: number) => void
  outputRows: InputRow[]
  dataSummary?: { name: string; count: number }[]
  pendingComment?: BatchCommentTarget | null
  onClearPendingComment?: () => void
  notProducedIds?: Set<string>
  onResetNotProduced?: () => void
  /** Called once a run this panel triggered completes successfully, so the
   * caller can bring the Gantt/results back into view (e.g. switch off the
   * changeovers/constraints/crew tab this panel is shown alongside). */
  onRunComplete?: () => void
}

// ─── Section Header ─────────────────────────────────────────────────────────

const SectionHeader = ({
  icon,
  title,
  rightElement,
}: {
  icon: React.ReactNode
  title: string
  rightElement?: React.ReactNode
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      pb: 1,
      mb: 1.5,
      borderBottom: '2px solid',
      borderColor: 'primary.main',
    }}
  >
    {icon}
    <Typography
      sx={{
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: 'primary.main',
      }}
    >
      {title}
    </Typography>
    {rightElement ? (
      <>
        <Box sx={{ flexGrow: 1 }} />
        {rightElement}
      </>
    ) : null}
  </Box>
)

// ─── Stat Card ──────────────────────────────────────────────────────────────

const StatCard = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: '#f8fafc',
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0, 65, 90, 0.08)',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '0.65rem',
          color: 'text.secondary',
          lineHeight: 1.2,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.9rem',
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1.3,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Paper>
)

// ─── Compact Config Field ───────────────────────────────────────────────────

const ConfigField = ({
  label,
  value,
  onChange,
  type = 'number',
  step,
  min,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  type?: string
  step?: number
  min?: number
}) => (
  <Box sx={{ flex: 1, minWidth: 120 }}>
    <Typography
      sx={{
        fontSize: '0.65rem',
        fontWeight: 600,
        color: 'text.secondary',
        mb: 0.25,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <TextField
      type={type}
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      inputProps={{ min: min ?? 0, step: step ?? 1 }}
      sx={{
        '& .MuiOutlinedInput-root': {
          bgcolor: 'white',
          fontSize: '0.75rem',
          '& input': { py: '5px', px: '8px' },
        },
      }}
    />
  </Box>
)

// ─── Schedule Stats ─────────────────────────────────────────────────────────

const ScheduleStats = memo(
  ({
    outputRows,
    weekBaseHour,
  }: {
    outputRows: InputRow[]
    /** From `weekBaseHourFor` — `null` for a single-week schedule, whose
     *  rows are already 0–168. Everything else here is a duration or a
     *  count and so is offset-free; the makespan is the one figure that
     *  reads an absolute hour, and without this a consolidated run's week 2
     *  reports the whole horizon's end. */
    weekBaseHour: number | null
  }) => {
    const stats = useMemo(() => {
      if (outputRows.length === 0) return null

      // Shared with every other makespan readout in the app, and with the
      // engine's own reported makespan. This used to be a local
      // max(line_end, kitchen_end). See makespan.ts.
      const makespan = makespanForWeek(outputRows, weekBaseHour)

      const uniqueLines = new Set(outputRows.map((r) => r.line))
      const uniqueKitchens = new Set(outputRows.map((r) => r.kitchen))
      const uniqueProducts = new Set(outputRows.map((r) => r.productCode))

      const totalProductionHours = outputRows.reduce((sum, r) => {
        const lineDuration = (r.line_end_time ?? 0) - (r.line_start_time ?? 0)
        return sum + Math.max(0, lineDuration)
      }, 0)
      const utilisation =
        uniqueLines.size > 0
          ? (totalProductionHours / (uniqueLines.size * HOURS_PER_WEEK)) * 100
          : 0

      const totalQuantity = outputRows.reduce(
        (sum, r) => sum + (r.quantity ?? 0),
        0
      )

      // Count changeovers: consecutive rows with different base_material per kitchen
      const kitchenRows = new Map<string, InputRow[]>()
      outputRows.forEach((r) => {
        const key = r.kitchen
        if (!kitchenRows.has(key)) kitchenRows.set(key, [])
        kitchenRows.get(key)!.push(r)
      })
      let changeovers = 0
      kitchenRows.forEach((rows) => {
        const sorted = [...rows].sort(
          (a, b) => (a.kitchen_start_time ?? 0) - (b.kitchen_start_time ?? 0)
        )
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i].base_material !== sorted[i - 1].base_material) {
            changeovers++
          }
        }
      })

      return {
        makespan,
        utilisation,
        totalQuantity,
        productCount: uniqueProducts.size,
        lineCount: uniqueLines.size,
        kitchenCount: uniqueKitchens.size,
        changeovers,
      }
    }, [outputRows, weekBaseHour])

    if (!stats) return null

    const formatHours = (hours: number) => {
      if (hours < 24) return `${hours.toFixed(1)}h`
      const days = Math.floor(hours / 24)
      const rem = hours % 24
      return `${days}d ${rem.toFixed(0)}h`
    }

    return (
      <Box sx={{ mb: 2 }}>
        <SectionHeader
          icon={<BarChartIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
          title="Schedule Stats"
        />
        <Grid container spacing={1}>
          <Grid size={6}>
            <StatCard
              icon={
                <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              }
              label="Makespan"
              value={formatHours(stats.makespan)}
            />
          </Grid>
          <Grid size={6}>
            <StatCard
              icon={
                <PercentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              }
              label="Utilisation"
              value={`${stats.utilisation.toFixed(1)}%`}
            />
          </Grid>
          <Grid size={6}>
            <StatCard
              icon={
                <InventoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              }
              label="Total Quantity"
              value={stats.totalQuantity.toLocaleString()}
            />
          </Grid>
          <Grid size={6}>
            <StatCard
              icon={
                <FactoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              }
              label="Products"
              value={stats.productCount}
            />
          </Grid>
          <Grid size={6}>
            <StatCard
              icon={
                <PrecisionManufacturingIcon
                  sx={{ fontSize: 18, color: 'primary.main' }}
                />
              }
              label="Lines Used"
              value={stats.lineCount}
            />
          </Grid>
          <Grid size={6}>
            <StatCard
              icon={
                <CountertopsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              }
              label="Kitchens Used"
              value={stats.kitchenCount}
            />
          </Grid>
          <Grid size={6}>
            <StatCard
              icon={
                <SwapHorizIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              }
              label="Changeovers"
              value={stats.changeovers}
            />
          </Grid>
        </Grid>
      </Box>
    )
  }
)

ScheduleStats.displayName = 'ScheduleStats'

// ─── Main Component ─────────────────────────────────────────────────────────

export const ScheduleControlPanel = memo(
  ({
    schedulerId,
    iteration,
    selectedWeek,
    onWeekChange,
    outputRows,
    dataSummary,
    pendingComment,
    onClearPendingComment,
    notProducedIds,
    onResetNotProduced,
    onRunComplete,
  }: ScheduleControlPanelProps) => {
    const navigate = useNavigate()
    const [fixBeforeHour, setFixBeforeHour] = useState(48)
    const [controlTab, setControlTab] = useState<
      'schedule' | 'data' | 'comments'
    >('schedule')

    // Auto-switch to comments tab when a batch is clicked for commenting
    const [commentText, setCommentText] = useState('')
    useMemo(() => {
      if (pendingComment) {
        setControlTab('comments')
        setCommentText('')
      }
    }, [pendingComment])

    // Comments
    const {
      data: comments = [],
      refetch: refetchComments,
      isLoading: isCommentsLoading,
    } = useSchedulerComments(schedulerId, iteration, selectedWeek)
    const onCommentCreateSuccess = useCallback(
      () => toast.success('Comment added'),
      []
    )
    const onCommentCreateError = useCallback(
      () => toast.error('Failed to add comment'),
      []
    )
    const { mutate: createComment } = useCreateComment(
      onCommentCreateSuccess,
      onCommentCreateError
    )
    const onCommentUpdateSuccess = useCallback(() => {}, [])
    const onCommentUpdateError = useCallback(
      () => toast.error('Failed to update comment'),
      []
    )
    const { mutate: updateComment } = useUpdateComment(
      onCommentUpdateSuccess,
      onCommentUpdateError
    )
    const onCommentDeleteSuccess = useCallback(
      () => toast.success('Comment deleted'),
      []
    )
    const onCommentDeleteError = useCallback(
      () => toast.error('Failed to delete comment'),
      []
    )
    const { mutate: deleteComment } = useDeleteComment(
      onCommentDeleteSuccess,
      onCommentDeleteError
    )

    const handleResolveComment = useCallback(
      (commentId: string, resolved: boolean) => {
        updateComment({ schedulerId, iteration, commentId, data: { resolved } })
      },
      [updateComment, schedulerId, iteration]
    )

    const handleDeleteComment = useCallback(
      (commentId: string) => {
        deleteComment({ schedulerId, iteration, commentId })
      },
      [deleteComment, schedulerId, iteration]
    )

    // Whose comments are the reader's own — this is what gates the delete
    // affordance. Read from MSAL: nothing in the Portal ever writes a
    // `userEmail` key, so the localStorage read this replaces always returned
    // '' and the delete button never appeared on anyone's own comment.
    const currentUserEmail = useCurrentUserEmail()

    const handleVersionChange = useCallback(
      (versionKey: string) => {
        navigate(`/plan-scheduler/input/${schedulerId}/${versionKey}`)
      },
      [navigate, schedulerId]
    )

    // Data staleness tracking
    // THE single background poller for data-status across the whole Portal.
    // The sidebar and the data-versions panel mount the same query and read
    // this result out of the cache; if another component starts polling too,
    // v3 gives it its own timer and the staggered refetches multiply what is
    // already the most expensive read here. Five minutes, not one: every
    // local write invalidates the query outright, so this only has to notice
    // another planner's edit.
    const { data: dataStatus } = useDataStatus(
      schedulerId,
      iteration,
      5 * 60 * 1000
    )
    const isDataStale = useMemo(() => {
      if (!dataStatus?.tables) return false
      return Object.values(dataStatus.tables).some((t) => t.modifiedSinceRun)
    }, [dataStatus])
    const editedTables = useMemo(() => {
      if (!dataStatus?.tables) return []
      return Object.entries(dataStatus.tables)
        .filter(([, t]) => t.modifiedSinceRun)
        .map(([name]) => name)
    }, [dataStatus])

    // GA config state
    const [popSize, setPopSize] = useState(100)
    const [generations, setGenerations] = useState(20)
    const [earlyStop, setEarlyStop] = useState(5)
    // Seed for the NSGA-II PRNG. Changing it explores a different region of
    // the Pareto front for the same inputs — useful when the makespan looks
    // stuck on a Pareto point that other seeds would beat. Default 42 keeps
    // run-to-run determinism unless the user deliberately changes it.
    const [seed, setSeed] = useState(42)
    // Advanced GA knobs — exploration vs exploitation tuning. Defaults
    // track NSGAII2Config::default() server-side. Raise mutation_prob and
    // lower tournament_size to widen exploration when the optimizer
    // appears to be missing better answers.
    const [mutationProb, setMutationProb] = useState(0.4)
    const [crossoverProb, setCrossoverProb] = useState(0.9)
    const [mutationRateAdaptivity, setMutationRateAdaptivity] = useState(0.5)
    const [tournamentSize, setTournamentSize] = useState(3)
    const [allowCleaningDuringDowntime, setAllowCleaningDuringDowntime] =
      useState(false)
    const [enableResourceYield, setEnableResourceYield] = useState(false)
    // Strictness toggles for missing changeover rules. Default true =
    // permissive (missing rule treated as free transition); flip to false to
    // drop products whose transitions have no defined SOR row.
    const [
      allowMissingBaseMaterialChangeover,
      setAllowMissingBaseMaterialChangeover,
    ] = useState(true)
    const [allowMissingProductChangeover, setAllowMissingProductChangeover] =
      useState(true)
    // Rule strictness (solver weights)
    const [wChangeoverCost, setWChangeoverCost] = useState(0)
    const [wChangeoverEnvCost, setWChangeoverEnvCost] = useState(0)
    const [wCrewConstraint, setWCrewConstraint] = useState(1)
    const [wMinrun, setWMinrun] = useState(1)
    const [wLate, setWLate] = useState(1)
    // Hours of makespan the solver will trade away per CIP avoided. 0 = off,
    // and off is the historical behaviour — clean count is already NSGA-II
    // objective F2, so the search prefers fewer cleans among otherwise-equal
    // schedules without this. Non-zero makes cleans able to outbid a shorter
    // schedule. Engine reads it from v7.35.0; before that the field existed
    // everywhere but nothing consumed it.
    const [wCleanCount, setWCleanCount] = useState(0)

    // Small-remainder demand adjustment gates. NOT edited here — the inputs
    // live in the Configuration Settings / Configure run dialog's "Small-run
    // removal" section. This state is the carrier that gets them from the
    // settings row onto the run this panel POSTs; deleting it would silently
    // drop both gates from a panel-driven run and hand the engine its own
    // defaults instead of what the planner saved. Defaults mirror the engine's
    // own (preprocess.rs: 2.0h / 10%) so an unconfigured schedule sends what
    // it would have run with anyway.
    const [smallBatchDropHours, setSmallBatchDropHours] = useState(2)
    const [smallBatchDropMaxFraction, setSmallBatchDropMaxFraction] =
      useState(0.1)
    // Survey breadth — carried from the settings row onto the POST the same
    // way as the gates above; edited in the Configure run dialogs.
    const [surveyProbeCount, setSurveyProbeCount] = useState(60)

    // Business priorities (user weights)
    const [wUserThroughput, setWUserThroughput] = useState(0.25)
    const [wUserService, setWUserService] = useState(0.25)
    const [wUserOps, setWUserOps] = useState(0.25)
    const [wUserBalance, setWUserBalance] = useState(0.25)

    // Tenant Configuration Settings — lowest-priority fallback for brand-new
    // schedules and the source the "Reset to SOR" button explicitly targets.
    const { data: configSettings } = useConfigurationSettings()
    // This plan's own saved "Configure run" override, if the user has ever
    // saved one — takes priority over both the tenant SOR and the last-run
    // snapshot below (an explicit save is a deliberate statement of intent
    // for the NEXT run). `isPlanOverride` is false when no such override
    // exists yet, in which case the backend has already fallen back to the
    // tenant SOR — that duplicate case is ignored below in favour of the
    // snapshot/SOR fallback so we don't skip showing "what this last ran
    // with".
    const { data: planConfigResult, dataUpdatedAt: planConfigUpdatedAt } =
      usePlanConfigurationSettings(schedulerId, iteration)
    const planOverride = planConfigResult?.isPlanOverride
      ? planConfigResult.data
      : null
    // Snapshot of the settings used on this iteration's last run. Preferred
    // over the tenant SOR for seeding so existing schedules show the values
    // they were last run with. Also still used to recover the
    // consolidated_weeks list after a browser refresh.
    const {
      data: scheduleSettingsSnapshot,
      isFetched: scheduleSettingsFetched,
    } = useScheduleSettings(schedulerId, iteration)
    // Backend returns `{ data: row, exists, status }`; pull the row out.
    const scheduleSettingsRow = scheduleSettingsSnapshot?.data ?? null
    const persistedConsolidatedWeeks = useMemo<number[] | undefined>(
      () =>
        parseConsolidatedWeeks(
          scheduleSettingsSnapshot?.data?.consolidated_weeks
        ),
      [scheduleSettingsSnapshot]
    )
    // Apply a settings object (either the SOR or a snapshot row) to every
    // panel state. Both ConfigurationSettings and SchedulerSettingsSnapshot
    // share these field names exactly, so this works for both.
    const applySettings = useCallback(
      (s: {
        pop_size: number
        generations: number
        early_stop: number
        seed: number
        mutation_prob: number
        crossover_prob: number
        mutation_rate_adaptivity: number
        tournament_size: number
        allow_cleaning_during_downtime: boolean
        enable_resource_yield: boolean
        allow_missing_base_material_changeover: boolean
        allow_missing_product_changeover: boolean
        w_changeover_cost: number
        w_changeover_env_cost: number
        w_crew_constraint: number
        w_minrun: number
        w_late: number
        w_user_throughput: number
        w_user_service: number
        w_user_ops: number
        w_user_balance: number
        // Optional because a snapshot written before `settings_schema()`
        // gained these columns reads them back as absent. Present-and-absent
        // must differ: an absent gate leaves the current value alone, while
        // 0 hours is a real setting that DISABLES the rule, so neither may be
        // coerced into the other.
        small_batch_drop_hours?: number | null
        small_batch_drop_max_fraction?: number | null
        survey_probe_count?: number | null
        w_clean_count?: number
      }) => {
        setPopSize(s.pop_size)
        setGenerations(s.generations)
        setEarlyStop(s.early_stop)
        setSeed(s.seed)
        setMutationProb(s.mutation_prob)
        setCrossoverProb(s.crossover_prob)
        setMutationRateAdaptivity(s.mutation_rate_adaptivity)
        setTournamentSize(s.tournament_size)
        setAllowCleaningDuringDowntime(s.allow_cleaning_during_downtime)
        setEnableResourceYield(s.enable_resource_yield)
        setAllowMissingBaseMaterialChangeover(
          s.allow_missing_base_material_changeover
        )
        setAllowMissingProductChangeover(s.allow_missing_product_changeover)
        setWChangeoverCost(s.w_changeover_cost)
        setWChangeoverEnvCost(s.w_changeover_env_cost)
        setWCrewConstraint(s.w_crew_constraint)
        setWMinrun(s.w_minrun)
        setWLate(s.w_late)
        setWUserThroughput(s.w_user_throughput)
        setWUserService(s.w_user_service)
        setWUserOps(s.w_user_ops)
        setWUserBalance(s.w_user_balance)
        // Tested as a number, not `!== undefined`: a snapshot row written
        // before these columns existed reads back as an explicit null, and
        // `!== undefined` would let that null through into numeric state.
        // Either way an unrecorded gate must leave the current value alone.
        if (typeof s.small_batch_drop_hours === 'number') {
          setSmallBatchDropHours(s.small_batch_drop_hours)
        }
        if (typeof s.small_batch_drop_max_fraction === 'number') {
          setSmallBatchDropMaxFraction(s.small_batch_drop_max_fraction)
        }
        // Null on an older snapshot or a default run — both mean 60.
        setSurveyProbeCount(
          typeof s.survey_probe_count === 'number' && s.survey_probe_count > 0
            ? s.survey_probe_count
            : 60
        )
        if (s.w_clean_count !== undefined) {
          setWCleanCount(s.w_clean_count)
        }
      },
      []
    )

    // Seed the panel's run-settings fields. Priority: this plan's saved
    // "Configure run" override, then the per-iteration last-run snapshot,
    // then the tenant SOR — in that order, because an explicit per-plan save
    // is a deliberate statement of intent for the NEXT run and should win
    // over history, while the snapshot still beats the SOR so a
    // never-explicitly-configured but previously-run schedule keeps showing
    // what it actually ran with. We wait for the snapshot fetch to settle
    // (`isFetched`) before falling back past it so a fast SOR fetch doesn't
    // beat the snapshot and trip the once-only guard.
    //
    // The plan-override branch re-fires whenever a NEW override lands
    // (tracked via `planConfigUpdatedAt`, react-query's fetch timestamp) —
    // not just on first mount — so saving via the "Configure run" dialog
    // while this panel is already open updates these fields immediately
    // (the dialog's save invalidates the same query key, which refetches
    // here since the panel is subscribed to it too). Once any branch has
    // seeded, `configSeededRef` blocks the snapshot/SOR fallback from
    // clobbering in-session edits the user hasn't saved; manual "Reset to
    // Configuration Settings" lives below.
    const configSeededRef = useRef(false)
    const appliedPlanOverrideAtRef = useRef(0)
    useEffect(() => {
      if (
        planOverride &&
        appliedPlanOverrideAtRef.current !== planConfigUpdatedAt
      ) {
        appliedPlanOverrideAtRef.current = planConfigUpdatedAt
        configSeededRef.current = true
        applySettings(planOverride)
        return
      }
      if (configSeededRef.current) return
      if (!scheduleSettingsFetched) return
      if (scheduleSettingsRow) {
        applySettings(scheduleSettingsRow)
        configSeededRef.current = true
        return
      }
      if (configSettings) {
        applySettings(configSettings)
        configSeededRef.current = true
      }
    }, [
      planOverride,
      planConfigUpdatedAt,
      configSettings,
      scheduleSettingsRow,
      scheduleSettingsFetched,
      applySettings,
    ])

    // Multi-week selection mode — 3-state machine:
    //   off       single-week behavior; button = "Multi-week" (outlined)
    //   selecting clicks toggle in multiSelectedWeeks; button = "Apply (N)"
    //   applied   stagger written; weeks stay highlighted; tile clicks
    //             navigate (single-week onWeekChange) without changing the
    //             multi-set; button = "Applied (N)" — click to revert.
    // The stagger formula: earliest selected week = hour 0; subsequent
    // weeks = (weekNumber - earliestSelected) * 168. Reverting writes null
    // back to dontStartBefore for the same weeks.
    //
    // Persistence: state is mirrored to localStorage so reloads don't drop
    // an in-progress selection or an applied stagger. Key includes
    // schedulerId+iteration so navigating between schedules doesn't leak
    // selections across them. On first mount with no stored entry we fall
    // back to seeding from the server snapshot (see effect below).
    const multiWeekStorageKey = `multiWeek_${schedulerId}_${iteration}`
    const [multiState, setMultiState] = useState<
      'off' | 'selecting' | 'applied'
    >(() => readStoredMultiWeek(multiWeekStorageKey)?.state ?? 'off')
    const [multiSelectedWeeks, setMultiSelectedWeeks] = useState<Set<number>>(
      () => new Set(readStoredMultiWeek(multiWeekStorageKey)?.weeks ?? [])
    )

    // Persist every change to localStorage. An empty 'off' / [] entry is
    // still written so we can distinguish "user deliberately reverted" from
    // "no entry yet" in the seed-from-snapshot effect below.
    useEffect(() => {
      writeStoredMultiWeek(multiWeekStorageKey, {
        state: multiState,
        weeks: Array.from(multiSelectedWeeks),
      })
    }, [multiWeekStorageKey, multiState, multiSelectedWeeks])

    // The list every week-scoped readout on this panel offsets against.
    // Source priority: the persisted snapshot from the last run (survives a
    // refresh) → in-session 'applied' state, which covers the gap before the
    // snapshot is readable. `undefined` means the rows are ordinary
    // per-week 0–168 times and must not be rebased at all.
    const effectiveConsolidatedWeeks = useMemo<number[] | undefined>(
      () =>
        persistedConsolidatedWeeks ??
        (multiState === 'applied' && multiSelectedWeeks.size >= 2
          ? Array.from(multiSelectedWeeks).sort((a, b) => a - b)
          : undefined),
      [persistedConsolidatedWeeks, multiState, multiSelectedWeeks]
    )

    // If localStorage had no entry on mount but the last run wrote
    // consolidated_weeks to the snapshot, seed the UI to 'applied' so the
    // button reads "Applied (N)" rather than "Multi-week" after a refresh.
    // Runs once per (schedulerId, iteration) — a deliberate revert (which
    // writes 'off' to localStorage) blocks re-seeding.
    const multiWeekSeedRef = useRef(false)
    useEffect(() => {
      if (multiWeekSeedRef.current) return
      if (readStoredMultiWeek(multiWeekStorageKey) !== null) {
        multiWeekSeedRef.current = true
        return
      }
      if (
        !persistedConsolidatedWeeks ||
        persistedConsolidatedWeeks.length < 2
      ) {
        return
      }
      multiWeekSeedRef.current = true
      setMultiState('applied')
      setMultiSelectedWeeks(new Set(persistedConsolidatedWeeks))
    }, [multiWeekStorageKey, persistedConsolidatedWeeks])

    const { mutate: applyStagger, isLoading: isApplyingStagger } =
      useApplyDontStartBeforeStagger(
        // Default callbacks are no-ops — each call passes specific
        // onSuccess/onError so apply vs unapply messages stay distinct and
        // state transitions happen only on actual success.
        () => undefined,
        () => undefined
      )

    const handleMultiWeekClick = useCallback(() => {
      if (multiState === 'off') {
        setMultiState('selecting')
        setMultiSelectedWeeks(new Set([selectedWeek]))
        return
      }
      if (multiState === 'selecting') {
        const weeks = Array.from(multiSelectedWeeks).sort((a, b) => a - b)
        if (weeks.length === 0) {
          // Nothing to apply — just exit back to single-week mode.
          setMultiState('off')
          return
        }
        const earliest = weeks[0]
        const weekToHour: Record<number, number | null> = {}
        weeks.forEach((w) => {
          weekToHour[w] = (w - earliest) * HOURS_PER_WEEK
        })
        applyStagger(
          { schedulerId, iteration, weekToHour },
          {
            onSuccess: () => {
              toast.success(
                `Don't-start-before stagger applied across ${weeks.length} week${
                  weeks.length === 1 ? '' : 's'
                }`
              )
              setMultiState('applied')
            },
            onError: () => {
              toast.error('Failed to apply multi-week stagger')
            },
          }
        )
        return
      }
      // multiState === 'applied' → revert: write null to dontStartBefore on
      // EVERY week (1-8), not just the ones we previously applied to. The
      // user may have changed selection between Apply and Unapply, and any
      // leftover stagger on an unselected week would silently constrain the
      // next single-week run. Clearing all 8 makes Unapply unambiguous.
      const weekToHour: Record<number, number | null> = {}
      for (let w = 1; w <= 8; w++) {
        weekToHour[w] = null
      }
      applyStagger(
        { schedulerId, iteration, weekToHour },
        {
          onSuccess: () => {
            toast.success("Don't-start-before cleared from all weeks")
            setMultiState('off')
            setMultiSelectedWeeks(new Set())
          },
          onError: () => {
            toast.error('Failed to clear multi-week stagger')
          },
        }
      )
    }, [
      multiState,
      multiSelectedWeeks,
      selectedWeek,
      applyStagger,
      schedulerId,
      iteration,
    ])

    const handleWeekTileClick = useCallback(
      (week: number) => {
        if (multiState === 'selecting') {
          setMultiSelectedWeeks((prev) => {
            const next = new Set(prev)
            if (next.has(week)) next.delete(week)
            else next.add(week)
            return next
          })
          return
        }
        // In 'off' OR 'applied' state: navigate weeks normally. The applied
        // highlight stays driven by multiSelectedWeeks so the user can still
        // see which weeks the stagger covers.
        onWeekChange(week)
      },
      [multiState, onWeekChange]
    )

    // Validation state
    const [validationOpen, setValidationOpen] = useState(false)
    // Comparison state
    const [comparisonOpen, setComparisonOpen] = useState(false)
    const [compareIteration, setCompareIteration] = useState<string | null>(
      null
    )

    // Fetch data for validation
    const { data: prodReqData } = useScheduleProductionRequirements(
      schedulerId,
      'production-requirements',
      iteration,
      selectedWeek
    )
    const { data: routesData } = useScheduleProductionRequirements(
      schedulerId,
      'packing-routes',
      iteration
    )
    const { data: downtimeValidation } = useScheduleProductionRequirements(
      schedulerId,
      'workstation-downtime',
      iteration,
      selectedWeek
    )
    const { data: constraintsValidation } = useScheduleProductionRequirements(
      schedulerId,
      'resource-and-material-constraints',
      iteration
    )

    const validationResults: ValidationResult[] = useMemo(() => {
      return runAllValidations({
        productionRequirements: prodReqData?.data,
        packagingRoutes: routesData?.data,
        downtime: downtimeValidation?.data,
        constraints: constraintsValidation?.data,
      })
    }, [prodReqData, routesData, downtimeValidation, constraintsValidation])

    const validationHasErrors = validationResults.some(
      (r) => r.severity === 'error'
    )

    // Browser notification helper
    const notifyScheduleComplete = useCallback(async (success: boolean) => {
      const originalTitle = document.title
      document.title = success
        ? 'Schedule Complete! - SupplyVue'
        : 'Schedule Failed - SupplyVue'
      const restore = () => {
        document.title = originalTitle
        document.removeEventListener('visibilitychange', restore)
      }
      document.addEventListener('visibilitychange', restore)
      setTimeout(restore, 30000)

      if (!('Notification' in window)) return
      if (Notification.permission === 'default')
        await Notification.requestPermission()
      if (Notification.permission === 'granted') {
        new Notification(success ? 'Schedule Complete' : 'Schedule Failed', {
          body: success
            ? 'Your scheduler run has finished successfully.'
            : 'The scheduler run encountered an error.',
        })
      }
    }, [])

    const queryClient = useQueryClient()
    const onRunSuccess = useCallback(() => {
      toast.success('Scheduler run completed successfully')
      notifyScheduleComplete(true)
      onResetNotProduced?.() // Clear hatching on any run completion
      queryClient.invalidateQueries(['scheduler', schedulerId, iteration])
      // Refetch the settings snapshot — scheduler_api wrote a fresh row
      // (status="complete") at the end of the run; the panel needs to pick
      // up the new timestamp + status chip.
      queryClient.invalidateQueries([
        'scheduler-settings',
        schedulerId,
        iteration,
      ])
      onRunComplete?.()
    }, [
      queryClient,
      schedulerId,
      iteration,
      notifyScheduleComplete,
      onResetNotProduced,
      onRunComplete,
    ])
    const onRunFailure = useCallback(
      (errorMessage?: string, failures?: RunFailure[]) => {
        toastRunFailure(
          'Why the run failed',
          errorMessage || 'Scheduler run failed',
          failures
        )
        notifyScheduleComplete(false)
      },
      [notifyScheduleComplete]
    )
    const {
      mutate: runScheduler,
      isLoading: isRunning,
      progress,
      weekResults,
      runResult,
    } = useRunSchedulerDirect(onRunSuccess, onRunFailure, {
      // Disabled and reading Running while this schedule runs, whoever
      // started it. The page's own hook reattaches to the progress.
      schedulerId,
    })

    // Preprocessing notices from the last run. Distinct from the pre-flight
    // validation above: those are computed here from the input tables before
    // running, these come back from the engine and say what it actually made
    // of that input.
    const engineNotices: ValidationResult[] = useMemo(
      () => noticesToValidationResults(runResult?.notices),
      [runResult]
    )

    const buildConfig = useCallback((): SchedulerRunConfig => {
      // When multi-week is APPLIED at Run time, ask scheduler_api for a
      // single consolidated solve covering the selected weeks. The
      // dontStartBefore values we wrote when entering 'applied' keep each
      // week's products gated to their week boundary; the extended horizon
      // gives the solve room to land them.
      const consolidatedWeeks =
        multiState === 'applied' && multiSelectedWeeks.size >= 2
          ? Array.from(multiSelectedWeeks).sort((a, b) => a - b)
          : undefined
      const tailBuffer = configSettings?.tail_buffer_hours ?? 400
      const timeHorizon = consolidatedWeeks
        ? consolidatedWeeks.length * HOURS_PER_WEEK + tailBuffer
        : undefined

      return {
        pop_size: popSize,
        generations,
        early_stop: earlyStop,
        seed,
        allow_cleaning_during_downtime: allowCleaningDuringDowntime,
        enable_resource_yield: enableResourceYield,
        w_changeover_cost: wChangeoverCost,
        w_changeover_env_cost: wChangeoverEnvCost,
        w_clean_count: wCleanCount,
        w_crew_constraint: wCrewConstraint,
        w_minrun: wMinrun,
        w_late: wLate,
        w_user_throughput: wUserThroughput,
        w_user_service: wUserService,
        w_user_ops: wUserOps,
        w_user_balance: wUserBalance,
        allow_missing_base_material_changeover:
          allowMissingBaseMaterialChangeover,
        allow_missing_product_changeover: allowMissingProductChangeover,
        mutation_prob: mutationProb,
        crossover_prob: crossoverProb,
        mutation_rate_adaptivity: mutationRateAdaptivity,
        tournament_size: tournamentSize,
        small_batch_drop_hours: smallBatchDropHours,
        small_batch_drop_max_fraction: smallBatchDropMaxFraction,
        survey_probe_count: surveyProbeCount,
        ...(timeHorizon ? { time_horizon: timeHorizon } : {}),
        ...(consolidatedWeeks ? { consolidated_weeks: consolidatedWeeks } : {}),
      }
    }, [
      popSize,
      generations,
      earlyStop,
      seed,
      allowCleaningDuringDowntime,
      enableResourceYield,
      wChangeoverCost,
      wChangeoverEnvCost,
      wCleanCount,
      wCrewConstraint,
      wMinrun,
      wLate,
      wUserThroughput,
      wUserService,
      wUserOps,
      wUserBalance,
      allowMissingBaseMaterialChangeover,
      allowMissingProductChangeover,
      mutationProb,
      crossoverProb,
      mutationRateAdaptivity,
      tournamentSize,
      smallBatchDropHours,
      smallBatchDropMaxFraction,
      surveyProbeCount,
      multiState,
      multiSelectedWeeks,
      configSettings,
    ])

    const doRunScheduler = useCallback(() => {
      runScheduler({
        schedulerId,
        iteration,
        config: buildConfig(),
      })
    }, [runScheduler, schedulerId, iteration, buildConfig])

    // Decides what is OFFERED only — the run is refused server-side by
    // security.auth.requires('run') regardless of what renders here.
    const { canRun } = usePermissions()

    const handleRun = useCallback(() => {
      onResetNotProduced?.()
      doRunScheduler()
    }, [doRunScheduler, onResetNotProduced])

    // Expand not-produced keys: kitchen click → all line products, all lines clicked → kitchen.
    // Shared with the Gantt view's own expansion (ScheduleFullGantt.view.tsx) — see
    // expandNotProducedKeys in Scheduler.data.ts for the key-format contract with scheduler_api.
    const expandNotProducedKeys = useCallback((): string[] => {
      if (!notProducedIds || notProducedIds.size === 0) return []
      return Array.from(expandNotProducedKeysShared(notProducedIds, outputRows))
    }, [notProducedIds, outputRows])

    // Lock & Re-run: pass fix params directly to the Rust scheduler
    const handleFixAndRerun = useCallback(() => {
      runScheduler({
        schedulerId,
        iteration,
        config: {
          ...buildConfig(),
          fix_before_hour: fixBeforeHour,
          not_produced_keys: expandNotProducedKeys(),
          // Without this, freeing up every pre-cutoff batch (e.g. clicking
          // every bar as not-produced) removes every downtime block
          // fix_before_hour would otherwise create, leaving the solver
          // free to backdate fresh production before the cutoff too. Same
          // hour as fix_before_hour — see MidWeekRescheduleDialog.view.tsx
          // for the fuller explanation of this mechanism.
          min_start_hour: fixBeforeHour,
        },
      })
    }, [
      runScheduler,
      schedulerId,
      iteration,
      buildConfig,
      fixBeforeHour,
      expandNotProducedKeys,
    ])

    const handleResetDefaults = useCallback(() => {
      // Pull the current tenant Configuration Settings (SOR) into the panel.
      // For existing schedules the panel is seeded from the per-iteration
      // snapshot, so this is the only way to deliberately re-sync to SOR
      // after the SOR has been edited. Falls back to hardcoded defaults if
      // the SOR fetch hasn't completed yet.
      const c: ConfigurationSettings | undefined = configSettings
      applySettings({
        pop_size: c?.pop_size ?? 100,
        generations: c?.generations ?? 20,
        early_stop: c?.early_stop ?? 5,
        seed: c?.seed ?? 42,
        mutation_prob: c?.mutation_prob ?? 0.4,
        crossover_prob: c?.crossover_prob ?? 0.9,
        mutation_rate_adaptivity: c?.mutation_rate_adaptivity ?? 0.5,
        tournament_size: c?.tournament_size ?? 3,
        allow_cleaning_during_downtime:
          c?.allow_cleaning_during_downtime ?? false,
        enable_resource_yield: c?.enable_resource_yield ?? false,
        allow_missing_base_material_changeover:
          c?.allow_missing_base_material_changeover ?? true,
        allow_missing_product_changeover:
          c?.allow_missing_product_changeover ?? true,
        w_changeover_cost: c?.w_changeover_cost ?? 0,
        w_changeover_env_cost: c?.w_changeover_env_cost ?? 0,
        w_clean_count: c?.w_clean_count ?? 0,
        w_crew_constraint: c?.w_crew_constraint ?? 1,
        w_minrun: c?.w_minrun ?? 1,
        w_late: c?.w_late ?? 1,
        w_user_throughput: c?.w_user_throughput ?? 0.25,
        w_user_service: c?.w_user_service ?? 0.25,
        w_user_ops: c?.w_user_ops ?? 0.25,
        w_user_balance: c?.w_user_balance ?? 0.25,
        // Fallbacks mirror the engine defaults in scheduler_rust types.rs.
        small_batch_drop_hours: c?.small_batch_drop_hours ?? 2,
        small_batch_drop_max_fraction: c?.small_batch_drop_max_fraction ?? 0.1,
        survey_probe_count: c?.survey_probe_count ?? 60,
      })
      toast.success('Reset to Configuration Settings')
    }, [configSettings, applySettings])

    const [outputModalOpen, setOutputModalOpen] = useState(false)
    const [gaOpen, setGaOpen] = useState(false)
    const [weightsOpen, setWeightsOpen] = useState(false)

    const hasData = outputRows.length > 0

    // Check which weeks have output data (uses React Query cache)
    const week1Data = useSchedulerData(schedulerId, iteration, 1)
    const week2Data = useSchedulerData(schedulerId, iteration, 2)
    const week3Data = useSchedulerData(schedulerId, iteration, 3)
    const week4Data = useSchedulerData(schedulerId, iteration, 4)
    const week5Data = useSchedulerData(schedulerId, iteration, 5)
    const week6Data = useSchedulerData(schedulerId, iteration, 6)
    const week7Data = useSchedulerData(schedulerId, iteration, 7)
    const week8Data = useSchedulerData(schedulerId, iteration, 8)

    const weekDataMap = useMemo(() => {
      const queries = [
        week1Data,
        week2Data,
        week3Data,
        week4Data,
        week5Data,
        week6Data,
        week7Data,
        week8Data,
      ]
      const map: Record<
        number,
        { hasData: boolean; productCount: number; makespan: number }
      > = {}
      queries.forEach((q, i) => {
        const rows = q.data?.data ?? []
        const makespan =
          rows.length > 0
            ? Math.max(
                ...rows.map((r: any) =>
                  Math.max(r.line_end_time ?? 0, r.kitchen_end_time ?? 0)
                )
              )
            : 0
        map[i + 1] = {
          hasData: rows.length > 0,
          productCount: new Set(rows.map((r: any) => r.productCode)).size,
          makespan,
        }
      })
      return map
    }, [
      week1Data.data,
      week2Data.data,
      week3Data.data,
      week4Data.data,
      week5Data.data,
      week6Data.data,
      week7Data.data,
      week8Data.data,
    ])

    // Parse version list for comparison dialog
    const versionEntries = useMemo(() => {
      try {
        const raw = week1Data.data?.schedule?.versionDetails
        if (!raw) return []
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return Object.keys(parsed).map((key) => ({
          key,
          name: parsed[key]?.name ?? `Version ${key}`,
        }))
      } catch {
        return []
      }
    }, [week1Data.data?.schedule?.versionDetails])

    const currentVersionName =
      versionEntries.find((v) => v.key === iteration)?.name ??
      `Version ${iteration}`

    const handleCompare = useCallback(() => {
      // Pick the first version that isn't the current one
      const other = versionEntries.find((v) => v.key !== iteration)
      if (other) {
        setCompareIteration(other.key)
        setComparisonOpen(true)
      } else {
        // Only one version — compare against itself (iteration 0)
        setCompareIteration('0')
        setComparisonOpen(true)
      }
    }, [versionEntries, iteration])

    return (
      <Paper
        elevation={0}
        sx={{
          width: PANEL_WIDTH,
          minWidth: PANEL_WIDTH,
          height: '100%',
          borderLeft: '1px solid #e0e0e0',
          overflow: 'auto',
          flexShrink: 0,
          bgcolor: '#fafbfc',
        }}
      >
        {/* Panel tabs */}
        <Tabs
          value={controlTab}
          onChange={(_, v) => setControlTab(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 36,
            px: 1,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
            },
          }}
        >
          <Tab label="Schedule" value="schedule" />
          <Tab label="Data & Snapshots" value="data" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Comments
                {comments.filter((c) => !c.resolved).length > 0 && (
                  <Chip
                    label={comments.filter((c) => !c.resolved).length}
                    size="small"
                    color="primary"
                    sx={{
                      height: 16,
                      fontSize: '0.55rem',
                      minWidth: 20,
                      '& .MuiChip-label': { px: 0.4 },
                    }}
                  />
                )}
              </Box>
            }
            value="comments"
          />
        </Tabs>

        {controlTab === 'data' && (
          <Box sx={{ p: 2 }}>
            <ScheduleDataVersionsPanel
              schedulerId={schedulerId}
              iteration={iteration}
              selectedWeek={selectedWeek}
              dataSummary={dataSummary}
              onVersionChange={handleVersionChange}
            />
            <Box sx={{ mt: 2 }}>
              <ScheduleSettingsSnapshotView
                schedulerId={schedulerId}
                iteration={iteration}
              />
            </Box>
          </Box>
        )}

        {controlTab === 'comments' && (
          <Box sx={{ p: 2 }}>
            {/* New comment input (when a bar was clicked) */}
            {pendingComment && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 1.5,
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(0,65,90,0.02)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    mb: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <Chip
                    label={pendingComment.productCode}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(0,65,90,0.08)',
                    }}
                  />
                  <Chip
                    label={`${pendingComment.resourceName} (${pendingComment.resourceType})`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.55rem' }}
                  />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  placeholder="Add a comment about this batch..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  autoFocus
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-input': { fontSize: '0.75rem' },
                  }}
                  size="small"
                />
                <Box
                  sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                >
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      onClearPendingComment?.()
                      setCommentText('')
                    }}
                    sx={{ fontSize: '0.68rem', textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!commentText.trim()}
                    startIcon={
                      <SendIcon sx={{ fontSize: '14px !important' }} />
                    }
                    onClick={() => {
                      createComment({
                        schedulerId,
                        iteration,
                        data: {
                          content: commentText.trim(),
                          productCode: pendingComment.productCode,
                          resourceName: pendingComment.resourceName,
                          resourceType: pendingComment.resourceType,
                          startTime: pendingComment.startTime,
                          endTime: pendingComment.endTime,
                          weekNumber: selectedWeek,
                        },
                      })
                      setCommentText('')
                      onClearPendingComment?.()
                    }}
                    sx={{ fontSize: '0.68rem', textTransform: 'none' }}
                  >
                    Comment
                  </Button>
                </Box>
              </Paper>
            )}
            <SchedulerCommentsPanel
              comments={comments}
              currentUserEmail={currentUserEmail}
              onRefresh={() => refetchComments()}
              onResolve={handleResolveComment}
              onDelete={handleDeleteComment}
              onCommentClick={(comment) => {
                // TODO: highlight bar on Gantt via onBatchSelect bridge
              }}
              isLoading={isCommentsLoading}
            />
          </Box>
        )}

        {controlTab === 'schedule' && (
          <Box sx={{ p: 2 }}>
            {/* Running banner */}
            {isRunning && (
              <Paper
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 1.5,
                  bgcolor: '#e3f2fd',
                  border: '1px solid #90caf9',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <CircularProgress size={18} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    Scheduler Running
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
                  >
                    Processing schedule... this may take a few minutes.
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Stale output banner */}
            {isDataStale && !isRunning && (
              <Paper
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 1.5,
                  bgcolor: '#fff3e0',
                  border: '1px solid #ffcc80',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#e65100',
                    mb: 0.5,
                  }}
                >
                  Output may be stale
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.62rem',
                    color: '#bf360c',
                    lineHeight: 1.4,
                  }}
                >
                  Data has been edited since the last scheduler run. Re-run to
                  update the schedule.
                </Typography>
                {editedTables.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      flexWrap: 'wrap',
                      mt: 0.75,
                    }}
                  >
                    {editedTables.map((name) => (
                      <Chip
                        key={name}
                        label={name.replace(/-/g, ' ')}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.58rem',
                          bgcolor: '#ffe0b2',
                          color: '#e65100',
                          '& .MuiChip-label': { px: 0.5 },
                          textTransform: 'capitalize',
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* ── Section 1: Week Overview ──────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
              <SectionHeader
                icon={
                  <ScheduleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                }
                title="Week Overview"
                rightElement={
                  <Button
                    size="small"
                    variant={multiState === 'off' ? 'outlined' : 'contained'}
                    color={multiState === 'applied' ? 'success' : 'primary'}
                    onClick={handleMultiWeekClick}
                    disabled={isApplyingStagger}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.6rem',
                      py: 0,
                      px: 1,
                      minHeight: 22,
                      lineHeight: 1.3,
                    }}
                  >
                    {isApplyingStagger
                      ? multiState === 'applied'
                        ? 'Clearing…'
                        : 'Applying…'
                      : multiState === 'applied'
                        ? `Applied (${multiSelectedWeeks.size})`
                        : multiState === 'selecting' &&
                            multiSelectedWeeks.size > 0
                          ? `Apply (${multiSelectedWeeks.size})`
                          : 'Multi-week'}
                  </Button>
                }
              />
              <Grid container spacing={0.75}>
                {Array.from({ length: 8 }, (_, i) => i + 1).map((week) => {
                  const isActive =
                    multiState !== 'off'
                      ? multiSelectedWeeks.has(week)
                      : week === selectedWeek
                  const weekHasData = weekDataMap[week]?.hasData ?? false
                  const weekProductCount = weekDataMap[week]?.productCount ?? 0
                  const weekMakespan = weekDataMap[week]?.makespan ?? 0
                  const wr = weekResults?.[week] as WeekResult | undefined
                  const isWeekRunning = wr?.status === 'running'
                  const isWeekComplete = wr?.status === 'complete'
                  const isWeekFailed = wr?.status === 'failed'

                  return (
                    <Grid size={3} key={week}>
                      <Paper
                        variant="outlined"
                        onClick={() => handleWeekTileClick(week)}
                        sx={{
                          p: 1,
                          cursor: 'pointer',
                          borderRadius: 1.5,
                          textAlign: 'center',
                          transition: 'all 0.15s ease',
                          borderColor: isWeekRunning
                            ? 'warning.main'
                            : isWeekComplete
                              ? 'success.main'
                              : isActive
                                ? 'primary.main'
                                : 'divider',
                          borderWidth:
                            isActive || isWeekRunning || isWeekComplete ? 2 : 1,
                          bgcolor: isWeekRunning
                            ? 'rgba(255, 152, 0, 0.04)'
                            : isWeekComplete
                              ? 'rgba(76, 175, 80, 0.04)'
                              : isActive
                                ? 'rgba(0, 65, 90, 0.04)'
                                : 'white',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'rgba(0, 65, 90, 0.02)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            mb: 0.25,
                          }}
                        >
                          {isActive && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight:
                                isActive || isWeekRunning || isWeekComplete
                                  ? 700
                                  : 600,
                              color: isWeekRunning
                                ? 'warning.dark'
                                : isWeekComplete
                                  ? 'success.dark'
                                  : isActive
                                    ? 'primary.main'
                                    : 'text.primary',
                            }}
                          >
                            Week {week}
                          </Typography>
                        </Box>
                        {isWeekRunning ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            <CircularProgress size={10} color="warning" />
                            <Typography
                              sx={{ fontSize: '0.6rem', color: 'warning.dark' }}
                            >
                              Optimising...
                            </Typography>
                          </Box>
                        ) : isWeekComplete ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{ fontSize: 12, color: 'success.main' }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.6rem',
                                  color: 'success.dark',
                                  fontWeight: 600,
                                }}
                              >
                                {wr!.makespan.toFixed(0)}h
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '0.55rem',
                                color: 'text.secondary',
                                lineHeight: 1.2,
                              }}
                            >
                              {wr!.rows} rows
                              {wr!.unmet_demand > 0 &&
                                ` · ${wr!.unmet_demand.toFixed(1)} unmet`}
                            </Typography>
                          </Box>
                        ) : isWeekFailed ? (
                          <Typography
                            sx={{ fontSize: '0.6rem', color: 'error.main' }}
                          >
                            Failed
                          </Typography>
                        ) : isRunning && !isWeekComplete ? (
                          <Typography
                            sx={{ fontSize: '0.6rem', color: 'text.disabled' }}
                          >
                            Pending...
                          </Typography>
                        ) : weekHasData ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{ fontSize: 12, color: 'success.main' }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.6rem',
                                  color: 'success.dark',
                                  fontWeight: 600,
                                }}
                              >
                                {weekMakespan.toFixed(0)}h
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '0.55rem',
                                color: 'text.secondary',
                                lineHeight: 1.2,
                              }}
                            >
                              {weekProductCount} products
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{ fontSize: '0.6rem', color: 'text.disabled' }}
                          >
                            &mdash;
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  )
                })}
              </Grid>

              {/* Quantity the small-remainder rule deleted from this run.
                  Renders nothing when the run dropped nothing (the normal
                  case). Run-time only — see ScheduleDemandAdjustments. */}
              {!isRunning && (
                <ScheduleDemandAdjustments
                  adjustments={runResult?.demand_adjustments}
                  unmetDemand={runResult?.unmet_demand}
                />
              )}

              {/* What preprocessing made of the input — a trial or carry-over
                  rejected, demand dropped for want of a route. Until now these
                  reached the API log and nowhere else, so a planner whose trial
                  was thrown out saw a clean run and no trial. Like the run
                  result itself these are not persisted, so they are gone once
                  the job is polled away; read them before navigating off. */}
              {!isRunning && engineNotices.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'text.secondary',
                      mb: 0.75,
                    }}
                  >
                    Data notices from this run
                  </Typography>
                  <SchedulerValidationPanel results={engineNotices} />
                </Box>
              )}

              {/* Overall progress bar when running */}
              {isRunning && progress && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor:
                      progress.stage === 'failed'
                        ? 'rgba(211, 47, 47, 0.04)'
                        : progress.stage === 'optimising'
                          ? 'rgba(255, 152, 0, 0.04)'
                          : 'rgba(0, 65, 90, 0.02)',
                    borderColor:
                      progress.stage === 'failed'
                        ? 'error.light'
                        : progress.stage === 'optimising'
                          ? 'warning.light'
                          : 'primary.light',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color:
                        progress.stage === 'failed'
                          ? 'error.main'
                          : progress.stage === 'optimising'
                            ? 'warning.dark'
                            : 'primary.main',
                      mb: 0.5,
                    }}
                  >
                    {progress.message}
                  </Typography>
                  {progress.stage === 'optimising' &&
                    progress.generation &&
                    progress.total_generations && (
                      <Box sx={{ mt: 0.5, mb: 0.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.25,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                          >
                            Generation {progress.generation}/
                            {progress.total_generations}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              color: 'warning.dark',
                            }}
                          >
                            {progress.week_makespan !== undefined
                              ? `${progress.week_makespan.toFixed(0)}h`
                              : ''}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={
                            (progress.generation / progress.total_generations) *
                            100
                          }
                          color="warning"
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    )}
                  {progress.elapsed_ms !== undefined && (
                    <Typography
                      sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                    >
                      {(progress.elapsed_ms / 1000).toFixed(1)}s elapsed
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ── Section 2: Schedule Stats ─────────────────────────────── */}
            <ScheduleStats
              outputRows={outputRows}
              weekBaseHour={weekBaseHourFor(
                effectiveConsolidatedWeeks,
                selectedWeek
              )}
            />

            {hasData && <Divider sx={{ mb: 2 }} />}

            {/* ── Section 3: Run Scheduler ──────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
              <SectionHeader
                icon={
                  <PlayArrowIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                }
                title="Run Scheduler"
              />

              {/* GA Settings */}
              <Paper
                variant="outlined"
                sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}
              >
                <Box
                  onClick={() => setGaOpen(!gaOpen)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <SpeedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      flex: 1,
                    }}
                  >
                    Genetic Algorithm
                  </Typography>
                  {gaOpen ? (
                    <ExpandLessIcon
                      sx={{ fontSize: 16, color: 'text.disabled' }}
                    />
                  ) : (
                    <ExpandMoreIcon
                      sx={{ fontSize: 16, color: 'text.disabled' }}
                    />
                  )}
                </Box>
                {gaOpen && (
                  <Box
                    sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}
                  >
                    <ConfigField
                      label="Population Size"
                      value={popSize}
                      onChange={setPopSize}
                      step={50}
                      min={50}
                    />
                    <ConfigField
                      label="Generations"
                      value={generations}
                      onChange={setGenerations}
                      step={5}
                      min={5}
                    />
                    <ConfigField
                      label="Early Stop"
                      value={earlyStop}
                      onChange={setEarlyStop}
                      step={1}
                      min={1}
                    />
                    <ConfigField
                      label="Seed"
                      value={seed}
                      onChange={setSeed}
                      step={1}
                      min={0}
                    />
                    {/* Advanced GA knobs — exploration vs exploitation.
                        Raise mutation, lower tournament_size to widen
                        exploration when the optimizer misses better
                        solutions. */}
                    <ConfigField
                      label="Mutation Prob"
                      value={mutationProb}
                      onChange={setMutationProb}
                      step={0.05}
                      min={0}
                    />
                    <ConfigField
                      label="Crossover Prob"
                      value={crossoverProb}
                      onChange={setCrossoverProb}
                      step={0.05}
                      min={0}
                    />
                    <ConfigField
                      label="Mutation Adaptivity"
                      value={mutationRateAdaptivity}
                      onChange={setMutationRateAdaptivity}
                      step={0.1}
                      min={0}
                    />
                    <ConfigField
                      label="Tournament Size"
                      value={tournamentSize}
                      onChange={setTournamentSize}
                      step={1}
                      min={2}
                    />
                  </Box>
                )}
              </Paper>

              {/* Rule Strictness */}
              <Paper
                variant="outlined"
                sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}
              >
                <Box
                  onClick={() => setWeightsOpen(!weightsOpen)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <GavelIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      flex: 1,
                    }}
                  >
                    Rule Strictness
                  </Typography>
                  {weightsOpen ? (
                    <ExpandLessIcon
                      sx={{ fontSize: 16, color: 'text.disabled' }}
                    />
                  ) : (
                    <ExpandMoreIcon
                      sx={{ fontSize: 16, color: 'text.disabled' }}
                    />
                  )}
                </Box>
                {weightsOpen && (
                  <>
                    <Box
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}
                    >
                      <ConfigField
                        label="Crew Constraint"
                        value={wCrewConstraint}
                        onChange={setWCrewConstraint}
                        step={0.5}
                      />
                      <ConfigField
                        label="Min Run Length"
                        value={wMinrun}
                        onChange={setWMinrun}
                        step={0.5}
                      />
                      <ConfigField
                        label="Late Penalty"
                        value={wLate}
                        onChange={setWLate}
                        step={0.5}
                      />
                      <ConfigField
                        label="Changeover Cost"
                        value={wChangeoverCost}
                        onChange={setWChangeoverCost}
                        step={0.1}
                      />
                      <ConfigField
                        label="Env Cost"
                        value={wChangeoverEnvCost}
                        onChange={setWChangeoverEnvCost}
                        step={0.1}
                      />
                      <ConfigField
                        label="Clean Count (hrs/clean)"
                        value={wCleanCount}
                        onChange={setWCleanCount}
                        step={0.1}
                      />
                    </Box>

                    {/* The small-run removal gates used to sit here. They are
                        not GA knobs — they delete demand — so they now have
                        their own section in the Configuration Settings /
                        Configure run dialog. The state below them still lives
                        in this component and is still POSTed with the run;
                        only the inputs moved. */}
                  </>
                )}
              </Paper>

              {/* Business Priorities */}
              <Paper
                variant="outlined"
                sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 1,
                  }}
                >
                  <BalanceIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    Business Priorities
                  </Typography>
                </Box>
                <Box sx={{ px: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Throughput
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      color: 'text.disabled',
                      lineHeight: 1.2,
                      mb: 0.25,
                    }}
                  >
                    Favour schedules that finish production sooner.
                  </Typography>
                  <Slider
                    size="small"
                    value={wUserThroughput}
                    onChange={(_, v) => setWUserThroughput(v as number)}
                    min={0}
                    max={1}
                    step={0.05}
                    valueLabelDisplay="auto"
                    sx={{ py: 0.5 }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Service
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      color: 'text.disabled',
                      lineHeight: 1.2,
                      mb: 0.25,
                    }}
                  >
                    Favour schedules that meet demand on time.
                  </Typography>
                  <Slider
                    size="small"
                    value={wUserService}
                    onChange={(_, v) => setWUserService(v as number)}
                    min={0}
                    max={1}
                    step={0.05}
                    valueLabelDisplay="auto"
                    sx={{ py: 0.5 }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Ops / Cost
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      color: 'text.disabled',
                      lineHeight: 1.2,
                      mb: 0.25,
                    }}
                  >
                    Favour fewer changeovers and lower cost.
                  </Typography>
                  <Slider
                    size="small"
                    value={wUserOps}
                    onChange={(_, v) => setWUserOps(v as number)}
                    min={0}
                    max={1}
                    step={0.05}
                    valueLabelDisplay="auto"
                    sx={{ py: 0.5 }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Balance
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      color: 'text.disabled',
                      lineHeight: 1.2,
                      mb: 0.25,
                    }}
                  >
                    Favour even workload across lines.
                  </Typography>
                  <Slider
                    size="small"
                    value={wUserBalance}
                    onChange={(_, v) => setWUserBalance(v as number)}
                    min={0}
                    max={1}
                    step={0.05}
                    valueLabelDisplay="auto"
                    sx={{ py: 0.5 }}
                  />
                </Box>
              </Paper>

              {/* Feature Toggle */}
              <Paper
                variant="outlined"
                sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowCleaningDuringDowntime}
                      onChange={(e) =>
                        setAllowCleaningDuringDowntime(e.target.checked)
                      }
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '0.7rem' }}>
                      Allow cleaning during downtime
                    </Typography>
                  }
                  sx={{ ml: 0, display: 'block' }}
                />
                {/* Strictness toggles for missing changeover rules.
                    ON  (default): missing rule → free transition (0h, no CIP).
                    OFF (strict) : missing rule → product dropped from schedule. */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowMissingBaseMaterialChangeover}
                      onChange={(e) =>
                        setAllowMissingBaseMaterialChangeover(e.target.checked)
                      }
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem' }}>
                        Allow missing base-material changeover
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.6rem', color: 'text.disabled' }}
                      >
                        On: missing rule = free transition. Off: drop the
                        product from the schedule.
                      </Typography>
                    </Box>
                  }
                  sx={{ ml: 0, display: 'block', mt: 0.5 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowMissingProductChangeover}
                      onChange={(e) =>
                        setAllowMissingProductChangeover(e.target.checked)
                      }
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem' }}>
                        Allow missing product changeover
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.6rem', color: 'text.disabled' }}
                      >
                        On: missing rule = free transition. Off: drop the
                        product from the schedule.
                      </Typography>
                    </Box>
                  }
                  sx={{ ml: 0, display: 'block', mt: 0.5 }}
                />
              </Paper>

              {/* Pre-flight Validation */}
              {validationResults.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: validationHasErrors
                      ? 'rgba(211,47,47,0.04)'
                      : 'rgba(237,108,2,0.04)',
                    borderColor: validationHasErrors
                      ? 'error.light'
                      : 'warning.light',
                  }}
                  onClick={() => setValidationOpen(!validationOpen)}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <PlaylistAddCheckIcon
                      sx={{
                        fontSize: 14,
                        color: validationHasErrors
                          ? 'error.main'
                          : 'warning.main',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: validationHasErrors
                          ? 'error.main'
                          : 'warning.main',
                        flex: 1,
                      }}
                    >
                      {validationResults.length} validation issue
                      {validationResults.length !== 1 ? 's' : ''}
                    </Typography>
                    {validationOpen ? (
                      <ExpandLessIcon
                        sx={{ fontSize: 16, color: 'text.disabled' }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        sx={{ fontSize: 16, color: 'text.disabled' }}
                      />
                    )}
                  </Box>
                  {validationOpen && (
                    <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                      <SchedulerValidationPanel results={validationResults} />
                    </Box>
                  )}
                </Paper>
              )}

              {/* Run Button */}
              <Button
                variant="contained"
                fullWidth
                onClick={handleRun}
                disabled={isRunning || validationHasErrors || !canRun}
                title={
                  canRun
                    ? undefined
                    : 'Your role does not allow running schedules.'
                }
                startIcon={
                  isRunning ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <PlayArrowIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={{
                  py: 1,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  mb: 1,
                }}
              >
                {!canRun
                  ? 'View only'
                  : isRunning
                    ? 'Running...'
                    : validationHasErrors
                      ? 'Fix Errors to Run'
                      : 'Run Scheduler'}
              </Button>

              {/* Reset to Configuration Settings (SOR) */}
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResetDefaults}
                  title="Reload the tenant Configuration Settings into this panel. Useful after the SOR has been updated."
                  sx={{
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  Reset to Configuration Settings
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ── Section 3b: Mid-Week Re-Plan ──────────────────────────── */}
            {hasData &&
              (() => {
                const DAY_OPTIONS = [
                  { label: 'Mon', hour: 24 },
                  { label: 'Tue', hour: 48 },
                  { label: 'Wed', hour: 72 },
                  { label: 'Thu', hour: 96 },
                  { label: 'Fri', hour: 120 },
                  { label: 'Sat', hour: 144 },
                  { label: 'Sun', hour: 168 },
                ]
                const rowKey = (r: any) =>
                  `${r.productCode}_${r.kitchen}_${r.kitchen_start_time}`
                const isNP = (r: any) => notProducedIds?.has(rowKey(r)) ?? false
                const lockedBatches = outputRows.filter(
                  (r) => (r.kitchen_start_time ?? 0) < fixBeforeHour && !isNP(r)
                )
                const notProducedBatches = outputRows.filter(isNP)
                const flexibleBatches = outputRows.filter(
                  (r) => (r.kitchen_start_time ?? 0) >= fixBeforeHour || isNP(r)
                )
                const lockedQty = lockedBatches.reduce(
                  (s, r) => s + (r.quantity ?? 0),
                  0
                )
                const flexibleQty = flexibleBatches.reduce(
                  (s, r) => s + (r.quantity ?? 0),
                  0
                )
                const flexibleProducts = new Set(
                  flexibleBatches.map((r) => r.productCode)
                ).size

                return (
                  <Box sx={{ mb: 2 }}>
                    <SectionHeader
                      icon={
                        <ScheduleIcon
                          sx={{ fontSize: 16, color: 'primary.main' }}
                        />
                      }
                      title="Mid-Week Re-Plan"
                    />
                    <Typography
                      sx={{
                        fontSize: '0.62rem',
                        color: 'text.secondary',
                        mb: 1.5,
                        lineHeight: 1.4,
                      }}
                    >
                      Click batches on the Gantt to mark as not produced, then
                      lock and re-run.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        color: 'text.secondary',
                        fontWeight: 600,
                        mb: 0.75,
                      }}
                    >
                      Lock production through:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
                      {DAY_OPTIONS.map((day) => (
                        <Paper
                          key={day.label}
                          variant="outlined"
                          onClick={() => setFixBeforeHour(day.hour)}
                          sx={{
                            flex: 1,
                            py: 0.5,
                            textAlign: 'center',
                            cursor: 'pointer',
                            borderRadius: 1,
                            transition: 'all 0.15s',
                            borderColor:
                              fixBeforeHour === day.hour
                                ? 'primary.main'
                                : 'divider',
                            borderWidth: fixBeforeHour === day.hour ? 2 : 1,
                            bgcolor:
                              fixBeforeHour === day.hour
                                ? 'rgba(0,65,90,0.06)'
                                : 'white',
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.62rem',
                              fontWeight:
                                fixBeforeHour === day.hour ? 700 : 500,
                              color:
                                fixBeforeHour === day.hour
                                  ? 'primary.main'
                                  : 'text.primary',
                            }}
                          >
                            {day.label}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        mb: 1.5,
                        borderRadius: 1.5,
                        bgcolor: '#f8fafc',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                        >
                          Locked (produced)
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.6rem', fontWeight: 700 }}
                        >
                          {lockedBatches.length} batches ·{' '}
                          {lockedQty.toLocaleString()} units
                        </Typography>
                      </Box>
                      {notProducedBatches.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              color: '#ed6c02',
                              fontWeight: 600,
                            }}
                          >
                            Not produced
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              color: '#ed6c02',
                            }}
                          >
                            {notProducedBatches.length} batches
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: 'primary.main',
                            fontWeight: 600,
                          }}
                        >
                          To re-optimise
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            color: 'primary.main',
                          }}
                        >
                          {flexibleBatches.length} batches ·{' '}
                          {flexibleQty.toLocaleString()} units ·{' '}
                          {flexibleProducts} products
                        </Typography>
                      </Box>
                    </Paper>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={isRunning}
                      onClick={handleFixAndRerun}
                      startIcon={
                        isRunning ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <PlayArrowIcon sx={{ fontSize: 16 }} />
                        )
                      }
                      sx={{
                        py: 0.75,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 1.5,
                      }}
                    >
                      {isRunning
                        ? 'Re-planning...'
                        : `Lock through ${DAY_OPTIONS.find((d) => d.hour === fixBeforeHour)?.label ?? ''} & Re-run`}
                    </Button>
                  </Box>
                )
              })()}

            <Divider sx={{ mb: 2 }} />

            {/* ── Section 4: Quick Actions ──────────────────────────────── */}
            <Box>
              <SectionHeader
                icon={
                  <OpenInNewIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                }
                title="Quick Actions"
              />
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setOutputModalOpen(true)}
                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                sx={{
                  py: 0.75,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  justifyContent: 'flex-start',
                  mb: 1,
                }}
              >
                View Full Output
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCompare}
                startIcon={<CompareArrowsIcon sx={{ fontSize: 16 }} />}
                sx={{
                  py: 0.75,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  justifyContent: 'flex-start',
                }}
              >
                Compare Versions
              </Button>
            </Box>
          </Box>
        )}

        {/* Full output modal */}
        <Dialog
          open={outputModalOpen}
          onClose={() => setOutputModalOpen(false)}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              width: '95vw',
              maxWidth: '95vw',
              height: '90vh',
              maxHeight: '90vh',
              borderRadius: 2,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>
              Factory Schedule
            </Typography>
            <IconButton size="small" onClick={() => setOutputModalOpen(false)}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: 0, overflow: 'auto' }}>
            <ScheduleFullGanttView
              schedulerId={schedulerId}
              iteration={iteration}
              selectedWeek={selectedWeek}
              onWeekChange={onWeekChange}
              // Multi-week CSV button + Gantt windowing both consume this.
              // Source priority: persisted snapshot from the last run
              // (survives refresh) → in-session 'applied' state (covers
              // the gap before the snapshot is readable).
              consolidatedWeeks={effectiveConsolidatedWeeks}
            />
          </DialogContent>
        </Dialog>
        {/* Comparison dialog */}
        {comparisonOpen && compareIteration && (
          <SchedulerComparison
            open={comparisonOpen}
            onClose={() => setComparisonOpen(false)}
            scheduleId={schedulerId}
            iterationA={iteration}
            iterationB={compareIteration}
            selectedWeek={selectedWeek}
            versionNameA={currentVersionName}
            versionNameB={
              versionEntries.find((v) => v.key === compareIteration)?.name ??
              `Version ${compareIteration}`
            }
          />
        )}
      </Paper>
    )
  }
)

ScheduleControlPanel.displayName = 'ScheduleControlPanel'
